/**
 * Sanitize admin-entered product description HTML for storage and storefront.
 * Browser-only (uses DOMParser). Safe subset: basic formatting + lists + span color/weight.
 */

const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'STRONG',
  'B',
  'I',
  'EM',
  'U',
  'UL',
  'OL',
  'LI',
  'SPAN',
  'DIV',
  'H3',
  'H4',
  'H5',
]);

function filterStyle(styleStr) {
  if (!styleStr || typeof styleStr !== 'string') return '';
  const parts = styleStr
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean);
  const out = [];
  for (const p of parts) {
    const low = p.toLowerCase();
    if (low.startsWith('color:') && !low.includes('url(') && !low.includes('expression(')) {
      out.push(p);
    } else if (low.startsWith('font-weight:')) {
      out.push(p);
    } else if (low.startsWith('text-align:')) {
      const v = p.split(':')[1]?.trim().toLowerCase();
      if (v === 'left' || v === 'right' || v === 'center' || v === 'justify') {
        out.push(p);
      }
    } else if (
      (low.startsWith('background:') || low.startsWith('background-image:')) &&
      !low.includes('url(') &&
      !low.includes('expression(')
    ) {
      out.push(p);
    } else if (low.startsWith('padding:') || low.startsWith('padding-top:') || low.startsWith('padding-left:')) {
      out.push(p);
    } else if (low.startsWith('border-left:') || low.startsWith('border-radius:')) {
      out.push(p);
    }
  }
  return out.join('; ');
}

function unwrap(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
}

function stripAttrs(el) {
  const tag = el.tagName;
  const rawStyle = el.hasAttribute('style') ? el.getAttribute('style') : null;
  [...el.attributes].forEach((a) => el.removeAttribute(a.name));
  if (rawStyle && ['SPAN', 'P', 'DIV', 'H3', 'H4', 'H5', 'LI'].includes(tag)) {
    const safe = filterStyle(rawStyle);
    if (safe) el.setAttribute('style', safe);
  }
}

export function sanitizeProductDescriptionHtml(html) {
  if (typeof html !== 'string' || !html.trim()) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div id="root">${html}</div>`, 'text/html');
    const root = doc.getElementById('root');
    if (!root) return '';

    let guard = 0;
    while (guard++ < 500) {
      const all = [...root.querySelectorAll('*')];
      const bad = all.filter((el) => !ALLOWED_TAGS.has(el.tagName));
      if (bad.length === 0) break;
      bad.forEach((el) => unwrap(el));
    }

    [...root.querySelectorAll('*')].forEach((el) => {
      if (ALLOWED_TAGS.has(el.tagName)) stripAttrs(el);
    });

    return root.innerHTML.trim();
  } catch {
    return '';
  }
}
